import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanGasWarningCenterCabinet } from "@domain/project/floorplan/floorplan-gas-warning-center-cabinet";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlaceholderPositionKind } from "@domain/project/floorplan/floorplan-placeholder-position-kind";
import { Expose, Type } from "class-transformer";
import { IsOptional } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanGasWarningCenterPlaceholder extends FloorplanPlaceholder {
  @Type(() => FloorplanGasWarningCenterCabinet)
  @IsOptional()
  @Expose({ name: "cabinet" })
  private _cabinet?: FloorplanGasWarningCenterCabinet;

  constructor(floorplan: Floorplan, id: string, x: number, y: number, size: number, notes?: string) {
    super(floorplan, id, x, y, size, notes);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.gasWarningCentersItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const size = this.size;

    return (x: number, y: number) => FloorplanGasWarningCenterPlaceholder.create(this._floorplan, x, y, size);
  }

  protected override copySpecificDuplicateStateTo(): ((duplicate: FloorplanItem) => void) | undefined {
    const cabinet = this.cabinet?.copy();
    const products = this.capturePlaceholderProducts();

    return (duplicate: FloorplanItem) => {
      const duplicatePlaceholder = duplicate as FloorplanGasWarningCenterPlaceholder;
      this.copyPlaceholderProductsTo(duplicatePlaceholder, products);
      if (cabinet) {
        duplicatePlaceholder._cabinet = cabinet.copy();
      }
    };
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.gasWarningCenterPlaceholders;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanGasWarningCenterPlaceholder,
        this._floorplan.gasWarningCenterPlaceholders,
        FloorplanEventType.GAS_WARNING_CENTER_PLACEHOLDER_ADDED,
        () => this.refreshPlaceholderPositionIds(),
        afterDuplicateAdded as ((duplicate: FloorplanGasWarningCenterPlaceholder) => void) | undefined,
      );
  }

  override delete() {
    this._floorplan.deleteGasWarningCenterPlaceholder(this);
  }

  get cabinet(): FloorplanGasWarningCenterCabinet | undefined {
    return this._cabinet;
  }

  set cabinet(value: FloorplanGasWarningCenterCabinet) {
    this._cabinet = value;
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshGasWarningCentersLock();
  }

  override getPlaceholderPositionKind(): FloorplanPlaceholderPositionKind | undefined {
    return FloorplanPlaceholderPositionKind.GAS_WARNING_CENTER;
  }

  static create(floorplan: Floorplan, x: number, y: number, size: number): FloorplanGasWarningCenterPlaceholder {
    return new FloorplanGasWarningCenterPlaceholder(floorplan, uuidv4(), x, y, size);
  }
}
